import React, { useMemo, useEffect, useState } from 'react';

const DEFAULT_SPECIAL_CHARS_REGEX = /[~`¿¡!#$%^&*€£@+÷=\-\[\]\\';,/{}\(\)|\\":<>\?\.\_]/;

const defaultMessages = {
    minLength: 'Password has at least %s characters',
    maxLength: 'Password has no more than %s characters',
    specialChar: 'Password has special characters',
    number: 'Password has a number',
    capital: 'Password has a capital letter',
    match: 'Passwords match',
    lowercase: 'Password has a lowercase letter',
    letter: 'Password has a letter',
    notEmpty: 'Password is not empty',
    capitalAndLowercase: 'Password has both capital and lowercase letters',
    noSpaces: 'Password has no spaces',
};

const ValidIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="7" cy="7" r="7" fill={color} />
        <path d="M3 7L6 10L11 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InvalidIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="7" cy="7" r="7" fill={color} />
        <path d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const NeutralIcon = ({ color }) => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="7" cy="7" r="6.25" stroke={color} strokeWidth="1.5" />
    </svg>
);

const Rule = ({
    valid,
    touched,
    text,
    validColor = '#4BCA81',
    invalidColor = '#FF0033',
    neutralColor = '#000000',
    validTextColor,
    invalidTextColor,
    iconComponents,
    hideIcon,
    rtl,
    className,
}) => {
    // Until the field is touched, keep every item neutral (black). Once touched,
    // each rule highlights independently: green when satisfied, red when not.
    const color = !touched ? neutralColor : (valid ? validColor : invalidColor);
    const textColor = !touched ? neutralColor : (valid ? validTextColor : invalidTextColor);

    const ValidIconComp = iconComponents?.ValidIcon || ValidIcon;
    const InvalidIconComp = iconComponents?.InvalidIcon || InvalidIcon;
    const NeutralIconComp = iconComponents?.NeutralIcon || NeutralIcon;

    const renderIcon = () => {
        if (!touched) return <NeutralIconComp color={neutralColor} />;
        return valid
            ? <ValidIconComp color={validColor} />
            : <InvalidIconComp color={invalidColor} />;
    };

    return (
        <li
            className={className}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexDirection: rtl ? 'row-reverse' : 'row',
                color: textColor,
                listStyle: 'none',
                fontSize: 13,
                margin: '2px 0',
            }}
        >
            {!hideIcon && renderIcon()}
            <span style={{ color }}>{text}</span>
        </li>
    );
};

const ReactPasswordChecklist = ({
    value = '',
    valueAgain,
    rules = [],
    minLength,
    maxLength,
    messages = {},
    onChange,
    validColor = '#4BCA81',
    invalidColor = '#FF0033',
    neutralColor = '#000000',
    validTextColor,
    invalidTextColor,
    iconComponents,
    hideIcon,
    rtl,
    className,
    style,
    itemClassName,
    specialCharsRegex,
    touched,
}) => {
    const regex = specialCharsRegex || DEFAULT_SPECIAL_CHARS_REGEX;

    // Track whether the password field has been interacted with. Items stay
    // neutral (black) until then, so we don't flag an untouched/empty field.
    // An explicit `touched` prop (e.g. from the form) always takes precedence.
    const [interacted, setInteracted] = useState(false);
    useEffect(() => {
        if (value.length > 0) setInteracted(true);
    }, [value]);
    const isTouched = touched || interacted;

    const ruleResults = useMemo(() => {
        const results = {};

        rules.forEach((rule) => {
            switch (rule) {
                case 'minLength':
                    results[rule] = value.length >= (minLength || 8);
                    break;
                case 'maxLength':
                    results[rule] = value.length <= (maxLength || 16);
                    break;
                case 'specialChar':
                    results[rule] = regex.test(value);
                    break;
                case 'number':
                    results[rule] = /\d/.test(value);
                    break;
                case 'capital':
                    results[rule] = /[A-Z]/.test(value);
                    break;
                case 'lowercase':
                    results[rule] = /[a-z]/.test(value);
                    break;
                case 'letter':
                    results[rule] = /[a-zA-Z]/.test(value);
                    break;
                case 'notEmpty':
                    results[rule] = value.length > 0 && (valueAgain === undefined || valueAgain.length > 0);
                    break;
                case 'capitalAndLowercase':
                    results[rule] = /[A-Z]/.test(value) && /[a-z]/.test(value);
                    break;
                case 'noSpaces':
                    results[rule] = !/\s/.test(value);
                    break;
                case 'match':
                    results[rule] = value.length > 0 && value === valueAgain;
                    break;
                default:
                    break;
            }
        });

        return results;
    }, [value, valueAgain, rules, minLength, maxLength, regex]);

    const isValid = useMemo(() => rules.every((rule) => ruleResults[rule]), [rules, ruleResults]);

    useEffect(() => {
        if (typeof onChange === 'function') {
            const failedRules = rules.filter((rule) => !ruleResults[rule]);
            onChange(isValid, failedRules);
        }
    }, [isValid, ruleResults]);

    const getMessage = (rule) => {
        if (messages[rule]) return messages[rule];
        const msg = defaultMessages[rule] || rule;
        if (rule === 'minLength') return msg.replace('%s', minLength || 8);
        if (rule === 'maxLength') return msg.replace('%s', maxLength || 16);
        return msg;
    };

    return (
        <ul
            className={className}
            style={{
                padding: '6px 0',
                margin: 0,
                ...style,
            }}
        >
            {rules.map((rule) => (
                <Rule
                    key={rule}
                    valid={!!ruleResults[rule]}
                    touched={isTouched}
                    text={getMessage(rule)}
                    validColor={validColor}
                    invalidColor={invalidColor}
                    neutralColor={neutralColor}
                    validTextColor={validTextColor}
                    invalidTextColor={invalidTextColor}
                    iconComponents={iconComponents}
                    hideIcon={hideIcon}
                    rtl={rtl}
                    className={itemClassName}
                />
            ))}
        </ul>
    );
};

export default ReactPasswordChecklist;
